import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Hotel, BadgePercent, MapPin, Calendar, Users, Search, MessageCircle,
  Sparkles, TrendingDown, Clock, CheckCircle2, AlertCircle, Building2, Plus, Euro
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import DateSelector14Days from '@/components/DateSelector14Days';
import CityAutocomplete from '@/components/CityAutocomplete';
import { useAuth } from '@/contexts/AuthContext';
import BookingModal from '@/components/BookingModalNew';
import TravelerLoginModal from '@/components/TravelerLoginModal';
import PropertyLoginModal from '@/components/PropertyLoginModal';
import RoleSelectionModal from '@/components/RoleSelectionModal';
import CreateOfferModal from '@/components/CreateOfferModal';
import CreateRequestModal from '@/components/CreateRequestModal';
import DealCardModern from '@/components/DealCardModern';
import PropertyCard from '@/components/PropertyCard';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import Logo from '@/components/Logo';
import AISearchChat from '@/components/AISearchChat';
import { generateFakeProperty, generateFakeTravelerProfile, FAKE_PROPERTY_PHOTOS } from '@/utils/fakeData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnifiedMarketplace = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isTraveler, logout } = useAuth();
  const [userType, setUserType] = useState(null);
  const [deals, setDeals] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [numberOfGuests, setNumberOfGuests] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [isRegistered, setIsRegistered] = useState(false);
  
  // Persistent session ID for conversation context
  const [sessionId, setSessionId] = useState(null);
  
  // Active filters from chat search
  const [activeFilters, setActiveFilters] = useState({
    city: null,
    budget: null,
    searchTerm: null
  });
  
  // Show/hide filters section
  const [showFilters, setShowFilters] = useState(true);
  
  // Booking modals
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showTravelerLoginModal, setShowTravelerLoginModal] = useState(false);
  const [showPropertyLoginModal, setShowPropertyLoginModal] = useState(false);
  const [showRoleSelectionModal, setShowRoleSelectionModal] = useState(false);
  
  // Offer modals (for property owners)
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  
  // Request modal (for travelers)
  const [showCreateRequestModal, setShowCreateRequestModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsRegistered(!!token);
    
    // Set initial chat message
    setChatMessages([{
      type: 'bot',
      text: t('chat.welcomeMessage')
    }]);
  }, [t]);

  useEffect(() => {
    if (userType) {
      fetchAllData();
    }
  }, [userType]);

  // Fetch data on page load for the preview section
  useEffect(() => {
    console.log('🔄 Fetching data on mount...');
    fetchAllData();
  }, []);

  useEffect(() => {
    console.log('Data updated - Deals:', deals.length, 'Requests:', requests.length);
    console.log('👤 userType:', userType, '- Should render preview?', !userType);
  }, [deals, requests, userType]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [dealsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/deals`),
        axios.get(`${API}/requests`)
      ]);
      setDeals(dealsRes.data.deals);
      setRequests(requestsRes.data.requests);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserTypeSelection = (type) => {
    setUserType(type);
    
    // Generate a persistent session ID for this conversation
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    setSessionId(newSessionId);
    
    const message = type === 'traveler' 
      ? t('chat.travelerResponse')
      : t('chat.propertyResponse');
    
    setChatMessages(prev => [
      ...prev,
      { type: 'user', text: type === 'traveler' ? t('userType.traveler') : t('userType.property') },
      { type: 'bot', text: message }
    ]);
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setChatLoading(true);

    try {
      // Call AI intelligent search endpoint with persistent session ID
      const response = await axios.post(`${API}/chat/intelligent-search`, {
        session_id: sessionId,
        message: userMessage,
        user_type: userType
      });

      const { response: aiResponse, extracted_data, matches_count } = response.data;
      
      // Set active filters from AI extracted data
      // The backend now remembers previous filters, so we get the complete filter state
      if (extracted_data) {
        const newFilters = {
          city: extracted_data.city || null,
          budget: extracted_data.budget || null,
          searchTerm: userMessage.toLowerCase()
        };
        setActiveFilters(newFilters);
      }
      
      // Hide filters when searching from chat
      setShowFilters(false);
      
      // Show AI response
      setChatMessages(prev => [...prev, { 
        type: 'bot', 
        text: aiResponse + (matches_count > 0 ? ` Trovati ${matches_count} risultati!` : '')
      }]);

      // Scroll to results
      setTimeout(() => {
        const section = userType === 'traveler' ? 'deals-section' : 'requests-section';
        document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
      }, 500);

    } catch (error) {
      console.error('Error:', error);
      setChatMessages(prev => [...prev, { 
        type: 'bot', 
        text: 'Si è verificato un errore. Riprova per favore.'
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleBookNow = (deal) => {
    if (!isAuthenticated() || !isTraveler()) {
      // User not logged in or not a traveler -> show login modal
      setSelectedDeal(deal);
      setShowTravelerLoginModal(true);
    } else {
      // User is logged in as traveler -> show booking modal
      setSelectedDeal(deal);
      setShowBookingModal(true);
    }
  };

  const handleTravelerLoginSuccess = (user) => {
    // After successful login, either show booking modal or redirect to dashboard
    setShowTravelerLoginModal(false);
    if (selectedDeal) {
      // User was trying to book a deal - show booking modal
      setShowBookingModal(true);
    } else {
      // User just logged in from button - redirect to dashboard
      toast.success(`Benvenuto, ${user?.full_name || 'Viaggiatore'}!`);
      navigate('/traveler/dashboard');
    }
  };

  const handlePropertyLoginSuccess = () => {
    // After property owner login, redirect to dashboard
    setShowPropertyLoginModal(false);
    navigate('/owner/dashboard');
  };

  const handleRoleSelection = (role, action) => {
    setShowRoleSelectionModal(false);
    
    if (action === 'login') {
      // Show appropriate login modal
      if (role === 'traveler') {
        setShowTravelerLoginModal(true);
      } else if (role === 'property') {
        setShowPropertyLoginModal(true);
      }
    } else if (action === 'register') {
      // Navigate to appropriate registration page
      if (role === 'traveler') {
        navigate('/traveler/register');
      } else if (role === 'property') {
        navigate('/property/register');
      }
    }
  };

  const handleBookingSuccess = (bookingData) => {
    toast.success('Richiesta di prenotazione inviata con successo!');
    setShowBookingModal(false);
    setSelectedDeal(null);
  };

  // Function to apply all active filters to deals
  const getFilteredDeals = () => {
    const filtered = deals.filter(deal => {
      // Filter by chat search city
      if (activeFilters.city && !deal.city?.toLowerCase().includes(activeFilters.city.toLowerCase())) {
        return false;
      }
      
      // Filter by selected city from autocomplete
      if (selectedCity && !deal.city?.toLowerCase().includes(selectedCity.toLowerCase())) {
        return false;
      }
      
      // Filter by budget
      if (activeFilters.budget && deal.price_per_night > activeFilters.budget) {
        return false;
      }
      if (maxBudget && deal.price_per_night > parseInt(maxBudget)) {
        return false;
      }
      
      // Filter by dates
      if (selectedDates.length > 0 && deal.available_dates) {
        const dealDates = deal.available_dates.map(d => d.date);
        const hasMatchingDate = selectedDates.some(selectedDate => 
          dealDates.includes(selectedDate)
        );
        if (!hasMatchingDate) return false;
      }
      
      // Filter by number of guests
      if (numberOfGuests && deal.max_guests) {
        if (parseInt(numberOfGuests) > deal.max_guests) return false;
      }
      
      return true;
    });
    return filtered;
  };

  // Function to apply all active filters to requests
  const getFilteredRequests = () => {
    const filtered = requests.filter(request => {
      // Filter by chat search city
      if (activeFilters.city && !request.city?.toLowerCase().includes(activeFilters.city.toLowerCase())) {
        return false;
      }
      
      // Filter by selected city from autocomplete
      if (selectedCity && !request.city?.toLowerCase().includes(selectedCity.toLowerCase())) {
        return false;
      }
      
      // Filter by budget - for property owners, show travelers with budget >= specified amount
      if (activeFilters.budget) {
        const travelerBudget = request.budget_per_night || request.budget_max || 0;
        if (travelerBudget < activeFilters.budget) return false;
      }
      if (maxBudget) {
        const travelerBudget = request.budget_per_night || request.budget_max || 0;
        if (travelerBudget < parseInt(maxBudget)) return false;
      }
      
      // Filter by dates
      if (selectedDates.length > 0 && request.check_in_date) {
        const requestDate = request.check_in_date;
        const hasMatchingDate = selectedDates.some(selectedDate => 
          requestDate === selectedDate || 
          (request.check_out_date && selectedDate >= requestDate && selectedDate <= request.check_out_date)
        );
        if (!hasMatchingDate) return false;
      }
      
      // Filter by number of guests
      if (numberOfGuests && request.guests) {
        if (parseInt(numberOfGuests) !== request.guests) return false;
      }
      
      return true;
    });
    return filtered;
  };

  // Enrich deals and requests with fake data
  const enrichedDeals = deals.map((deal, index) => {
    const fakeProperty = generateFakeProperty(index);
    return {
      ...deal,
      property_details: {
        ...deal.property_details,
        name: fakeProperty.name,
        photos: fakeProperty.photos,
        rating: fakeProperty.rating,
        reviews: fakeProperty.reviews
      },
      image_url: fakeProperty.photos[0],
      city: deal.city || fakeProperty.city
    };
  });

  const enrichedRequests = requests.map((request, index) => {
    const fakeTraveler = generateFakeTravelerProfile(index);
    return {
      ...request,
      traveler_info: fakeTraveler,
      traveler_photo: fakeTraveler.photo,
      traveler_name: `${fakeTraveler.nome} ${fakeTraveler.cognome}`
    };
  });

  // Function to merge and sort all items chronologically
  const getAllItemsChronological = () => {
    const dealsWithType = enrichedDeals.map(deal => ({
      ...deal,
      type: 'deal',
      dateCreated: deal.created_at || deal.check_in,
      sortDate: new Date(deal.created_at || deal.check_in || Date.now())
    }));
    
    const requestsWithType = enrichedRequests.map(request => ({
      ...request,
      type: 'request',
      dateCreated: request.created_at || request.check_in_date,
      sortDate: new Date(request.created_at || request.check_in_date || Date.now())
    }));
    
    // Merge and sort by date (newest first)
    const allItems = [...dealsWithType, ...requestsWithType];
    return allItems.sort((a, b) => b.sortDate - a.sortDate);
  };

  // Function to clear all filters
  const clearAllFilters = () => {
    // Generate a new session ID to clear conversation context
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    setSessionId(newSessionId);
    
    setActiveFilters({ city: null, budget: null, searchTerm: null });
    setSelectedCity('');
    setSelectedDates([]);
    setMaxBudget('');
    setNumberOfGuests('');
    setChatMessages(prev => [...prev, { 
      type: 'bot', 
      text: 'Filtri rimossi! Ora visualizzi tutti i risultati disponibili.' 
    }]);
  };
  
  // Function to start a new search
  const startNewSearch = () => {
    // Generate a new session ID to start fresh conversation
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    setSessionId(newSessionId);
    
    setActiveFilters({ city: null, budget: null, searchTerm: null });
    setSelectedCity('');
    setSelectedDates([]);
    setMaxBudget('');
    setNumberOfGuests('');
    setShowFilters(true);
    setChatMessages(prev => [...prev, { 
      type: 'bot', 
      text: 'Nuova ricerca avviata! Usa la chat o i filtri qui sotto per trovare quello che cerchi.' 
    }]);
  };
  
  // Check if any filters are active
  const hasActiveFilters = () => {
    return activeFilters.city || activeFilters.budget || activeFilters.searchTerm || 
           selectedCity || selectedDates.length > 0 || maxBudget || numberOfGuests;
  };

  const getStatusConfig = (status) => {
    const configs = {
      APERTO: { 
        color: 'bg-emerald-500', 
        textColor: 'text-emerald-700',
        bgLight: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        icon: CheckCircle2, 
        text: t('status.available')
      },
      APERTA: { 
        color: 'bg-emerald-500', 
        textColor: 'text-emerald-700',
        bgLight: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        icon: CheckCircle2, 
        text: t('status.open')
      },
      IN_TRATTATIVA: { 
        color: 'bg-amber-500', 
        textColor: 'text-amber-700',
        bgLight: 'bg-amber-50',
        borderColor: 'border-amber-200',
        icon: Clock, 
        text: t('status.inProgress')
      },
      CONCLUSO: { 
        color: 'bg-slate-400', 
        textColor: 'text-slate-600',
        bgLight: 'bg-slate-50',
        borderColor: 'border-slate-200',
        icon: CheckCircle2, 
        text: t('status.completed')
      },
      ESAURITO: { 
        color: 'bg-slate-400', 
        textColor: 'text-slate-600',
        bgLight: 'bg-slate-50',
        borderColor: 'border-slate-200',
        icon: AlertCircle, 
        text: t('status.soldOut')
      }
    };
    return configs[status] || configs.APERTO;
  };

  const getLabelConfig = (label) => {
    const labels = {
      'tonight': { icon: Clock, text: t('label.tonight'), color: 'bg-violet-500' },
      'last-minute': { icon: Sparkles, text: t('label.lastMinute'), color: 'bg-rose-500' },
      'weekend': { icon: TrendingDown, text: t('label.weekend'), color: 'bg-orange-500' }
    };
    return labels[label] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <nav className="backdrop-blur-xl bg-white/80 border-b border-slate-200/50 px-2 sm:px-6 py-2 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Logo size="normal" showText={true} />
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {userType && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setUserType(null);
                  setSessionId(null);
                  setActiveFilters({ city: null, budget: null, searchTerm: null });
                  setChatMessages([{
                    type: 'bot',
                    text: t('chat.welcomeMessage')
                  }]);
                }}
                className="text-slate-600 hidden sm:flex"
              >
                {t('nav.switchRole')}
              </Button>
            )}
            {isAuthenticated() ? (
              <>
                {isTraveler() && (
                  <>
                    <button
                      onClick={() => navigate('/traveler/dashboard')}
                      className="flex items-center gap-2 px-2 sm:px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all cursor-pointer"
                    >
                      <Users className="w-4 h-4 text-blue-600" />
                      <span className="text-xs sm:text-sm font-medium text-blue-900 hidden sm:inline">{user?.full_name}</span>
                    </button>
                  </>
                )}
                {user?.role === 'property_owner' && (
                  <>
                    <div className="flex items-center gap-2 px-2 sm:px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                      <Building2 className="w-4 h-4 text-orange-600" />
                      <span className="text-xs sm:text-sm font-medium text-orange-900 hidden sm:inline">{user?.full_name}</span>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/owner/dashboard')}>
                      Dashboard
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => { 
                  logout();
                  toast.success(t('message.loggedOut'));
                }}>
                  Logout
                </Button>
              </>
            ) : (
              <Button 
                onClick={() => setShowRoleSelectionModal(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/30 text-xs sm:text-sm px-2 sm:px-4 py-2"
              >
                <span className="sm:hidden">Accedi</span>
                <span className="hidden sm:inline">Accedi / Registrati</span>
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* AI Search Section */}
        <div className="mb-8">
          <AISearchChat 
            onSearch={async (query) => {
              setChatLoading(true);
              setActiveFilters({ ...activeFilters, city: query, searchTerm: query });
              
              // Search in deals and requests
              try {
                const [dealsRes, requestsRes] = await Promise.all([
                  axios.get(`${API}/deals`),
                  axios.get(`${API}/requests`)
                ]);
                
                const allDeals = dealsRes.data?.deals || dealsRes.data || [];
                const allRequests = requestsRes.data?.requests || [];
                
                // Filter by city
                const filteredDeals = allDeals.filter(d => 
                  d.city?.toLowerCase().includes(query.toLowerCase())
                );
                const filteredRequests = allRequests.filter(r => 
                  r.city?.toLowerCase().includes(query.toLowerCase())
                );
                
                setDeals(filteredDeals);
                setRequests(filteredRequests);
                
                toast.success(`Trovati ${filteredDeals.length} deals e ${filteredRequests.length} richieste per "${query}"`);
              } catch (error) {
                console.error('Search error:', error);
                toast.error('Errore nella ricerca');
              } finally {
                setChatLoading(false);
              }
            }}
            isLoading={chatLoading}
            placeholder="Inserisci una città (es: Roma, Milano, Firenze...)"
          />
        </div>

        {/* Role Selection for non-authenticated users */}
        {!userType && !isAuthenticated() && (
          <Card className="backdrop-blur-sm bg-white/70 border border-slate-200/50 shadow-xl p-4 sm:p-8 mb-4 sm:mb-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Come vuoi iniziare?</h2>
              <p className="text-slate-600">Seleziona il tuo ruolo per un'esperienza personalizzata</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <button
                onClick={() => handleUserTypeSelection('traveler')}
                className="group relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-blue-500/30 min-h-[120px] sm:min-h-auto"
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-white/0 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <MapPin className="w-8 h-8" strokeWidth={2} />
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-lg mb-1">{t('userType.traveler')}</p>
                    <p className="text-sm text-blue-100">{t('userType.travelerDesc')}</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleUserTypeSelection('property')}
                className="group relative overflow-hidden bg-gradient-to-br from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-orange-500/30 min-h-[120px] sm:min-h-auto"
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-white/0 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Building2 className="w-8 h-8" strokeWidth={2} />
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-lg mb-1">{t('userType.property')}</p>
                    <p className="text-sm text-orange-100">{t('userType.propertyDesc')}</p>
                  </div>
                </div>
              </button>
            </div>
          </Card>
        )}

        {/* Original Chat Assistant Card - hidden but kept for conversation context */}
        {userType && (
        <Card className="backdrop-blur-sm bg-white/70 border border-slate-200/50 shadow-xl p-4 sm:p-8 mb-4 sm:mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
              <MessageCircle className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-lg">{t('chat.assistant')}</p>
              <p className="text-sm text-slate-600">
                {userType === 'traveler' 
                  ? t('chat.travelerContext')
                  : userType === 'property'
                  ? t('chat.propertyContext')
                  : t('chat.defaultContext')}
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 mb-6 max-h-96 overflow-y-auto">
            <div className="space-y-4">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-2xl ${
                    msg.type === 'user'
                      ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/20'
                      : 'bg-white text-slate-800 shadow-md border border-slate-200/50'
                  } rounded-2xl px-6 py-4`}>
                    <p className="text-sm leading-relaxed whitespace-pre-line">{msg.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleChatSubmit} className="flex gap-3">
            <Input
              placeholder={userType === 'traveler' 
                ? t('chat.placeholderTraveler')
                : t('chat.placeholderProperty')}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              disabled={chatLoading}
              className="flex-1 h-12 bg-white border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
            <Button 
              type="submit" 
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 h-12 px-8 shadow-lg shadow-blue-500/30"
              disabled={chatLoading}
            >
              {chatLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : t('chat.send')}
            </Button>
          </form>
        </Card>
        )}

        {/* Preview Section - Always visible BELOW role selection buttons */}
        {!userType && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-6 h-6 text-blue-600" />
              <h2 className="text-3xl font-bold text-slate-900">Ultime Offerte e Richieste</h2>
              <Badge className="bg-blue-100 text-blue-800 px-3 py-1">
                {deals.length + requests.length} disponibili
              </Badge>
            </div>
            
            {loading ? (
              <div className="text-center py-20">
                <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
              </div>
            ) : getAllItemsChronological().length === 0 ? (
              <Card className="p-16 text-center bg-white/50">
                <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 text-lg">Nessun risultato disponibile al momento.</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getAllItemsChronological().slice(0, 12).map((item) => {
                  const isDeal = item.type === 'deal';
                  
                  return isDeal ? (
                    <DealCardModern
                      key={`${item.type}-${item.id}`}
                      deal={item}
                      onBook={() => navigate('/traveler/register')}
                      onCounterOffer={() => navigate('/traveler/register')}
                    />
                  ) : (
                    <Card
                      key={`${item.type}-${item.id}`}
                      className="group overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.01]"
                      onClick={() => navigate('/property/register')}
                    >
                      {/* Request Card with Traveler Info */}
                      <div className="relative h-48 bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700 overflow-hidden">
                        <div className="absolute inset-0 bg-black/20"></div>
                        <img 
                          src={item.traveler_photo}
                          alt={item.traveler_name}
                          className="w-24 h-24 rounded-full border-4 border-white shadow-2xl object-cover absolute bottom-[-48px] left-1/2 transform -translate-x-1/2 group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                          <Badge className="bg-emerald-600 text-white border-0">
                            Richiesta
                          </Badge>
                        </div>
                      </div>

                      <div className="pt-16 pb-6 px-6">
                        <div className="text-center mb-4">
                          <h3 className="text-xl font-bold text-slate-900 mb-1">{item.traveler_name}</h3>
                          <p className="text-sm text-slate-600">Cerca alloggio a {item.city}</p>
                        </div>

                        <div className="space-y-3 mb-4">
                          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-slate-600">
                              <Calendar className="w-4 h-4" />
                              <span className="text-sm">{item.check_in_date} → {item.check_out_date}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-slate-600">
                              <Users className="w-4 h-4" />
                              <span className="text-sm">{item.guests} ospiti</span>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-black text-emerald-600">€{item.budget_min}-{item.budget_max}</div>
                              <div className="text-xs text-slate-500">Budget</div>
                            </div>
                          </div>
                        </div>

                        {item.notes && (
                          <div className="bg-emerald-50 rounded-lg p-3 mb-4">
                            <p className="text-sm text-emerald-900 line-clamp-2">{item.notes}</p>
                          </div>
                        )}

                        <Button
                          onClick={() => navigate('/property/register')}
                          className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 h-12"
                        >
                        <Building2 className="w-4 h-4 mr-1" />
                          Registrati e Fai un'Offerta
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
            
            <div className="mt-8 text-center">
              <p className="text-slate-600 text-lg mb-4">
                Seleziona il tuo ruolo sopra per iniziare!
              </p>
            </div>
          </div>
        )}

        {userType && (
          <>
            {/* Active Filters Badge */}
            {hasActiveFilters() && !showFilters && (
              <Card className="backdrop-blur-sm bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 p-4 mb-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Search className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-900">Filtri attivi:</span>
                    {activeFilters.city && (
                      <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                        <MapPin className="w-3 h-3 mr-1" /> {activeFilters.city}
                      </Badge>
                    )}
                    {activeFilters.budget && (
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        Max €{activeFilters.budget}
                      </Badge>
                    )}
                    {selectedCity && (
                      <Badge className="bg-purple-100 text-purple-800 border-purple-300">
                        {selectedCity}
                      </Badge>
                    )}
                    {selectedDates.length > 0 && (
                      <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                        <Calendar className="w-3 h-3 mr-1" /> {selectedDates.length} {selectedDates.length === 1 ? 'data' : 'date'}
                      </Badge>
                    )}
                    {maxBudget && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                        Max €{maxBudget}
                      </Badge>
                    )}
                    {numberOfGuests && (
                      <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300">
                        <Users className="w-3 h-3 mr-1" /> {numberOfGuests} {numberOfGuests === '1' ? 'persona' : 'persone'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowFilters(true)}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      Mostra filtri
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={startNewSearch}
                      className="border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      🔄 Nuova ricerca
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Collapsible Filters Section */}
            {showFilters && (
              <Card className="backdrop-blur-sm bg-white/90 border border-slate-200/50 p-6 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-900">{t('filters.title')}</h3>
                  </div>
                  {hasActiveFilters() && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowFilters(false)}
                      className="text-slate-600 hover:text-slate-900"
                    >
                      Nascondi filtri
                    </Button>
                  )}
                </div>
                
                <div className="mb-6">
                  <DateSelector14Days onDatesChange={setSelectedDates} />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <CityAutocomplete 
                    value={selectedCity}
                    onChange={setSelectedCity}
                    placeholder={t('filters.city')}
                  />
                  <Input 
                    type="number" 
                    placeholder={t('filters.maxBudget')} 
                    className="bg-white"
                    value={maxBudget}
                    onChange={(e) => setMaxBudget(e.target.value)}
                  />
                  <Input 
                    type="number" 
                    placeholder="Numero Persone" 
                    className="bg-white"
                    min="1"
                    max="10"
                    value={numberOfGuests}
                    onChange={(e) => setNumberOfGuests(e.target.value)}
                  />
                  <Button 
                    className="bg-slate-900 hover:bg-slate-800"
                    onClick={() => {
                      const filterParts = [];
                      if (selectedCity) filterParts.push(selectedCity);
                      if (maxBudget) filterParts.push(`€${maxBudget}`);
                      if (selectedDates.length > 0) filterParts.push(`${selectedDates.length} date`);
                      if (numberOfGuests) filterParts.push(`${numberOfGuests} persone`);
                      
                      setShowFilters(false);
                      
                      const count = userType === 'traveler' ? getFilteredDeals().length : getFilteredRequests().length;
                      const message = filterParts.length > 0 
                        ? `Filtri applicati: ${filterParts.join(', ')}. Trovati ${count} risultati.`
                        : 'Nessun filtro selezionato.';
                      
                      setChatMessages(prev => [...prev, { 
                        type: 'bot', 
                        text: message
                      }]);
                      
                      setTimeout(() => {
                        const section = userType === 'traveler' ? 'deals-section' : 'requests-section';
                        document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
                      }, 300);
                    }}
                  >
                    {t('filters.search')}
                  </Button>
                </div>
              </Card>
            )}

            {/* Deals Section for Travelers */}
            {userType === 'traveler' && (
              <div id="deals-section" className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                  <h2 className="text-3xl font-bold text-slate-900">{t('deals.title')}</h2>
                </div>
                
                {loading ? (
                  <div className="text-center py-20">
                    <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                  </div>
                ) : getFilteredDeals().length === 0 ? (
                  <Card className="p-16 text-center bg-white/50">
                    <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 text-lg">
                      {deals.length === 0 ? t('deals.noDeals') : 'Nessun deal trovato con i filtri attuali.'}
                    </p>
                    {hasActiveFilters() && (
                      <Button 
                        variant="outline" 
                        onClick={clearAllFilters}
                        className="mt-4"
                      >
                        Rimuovi tutti i filtri
                      </Button>
                    )}
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                    {getFilteredDeals().map((deal) => {
                      const statusConfig = getStatusConfig(deal.status);
                      const labelConfig = getLabelConfig(deal.label);
                      const isConcluded = deal.status === 'CONCLUSO' || deal.status === 'ESAURITO';
                      const StatusIcon = statusConfig.icon;

                      return (
                        <Card
                          key={deal.id}
                          className={`group backdrop-blur-sm bg-white/80 border border-slate-200/50 p-3 sm:p-6 transition-all duration-300 ${
                            isConcluded 
                              ? 'opacity-50' 
                              : 'hover:shadow-2xl hover:scale-[1.01] hover:border-blue-300/50'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                            <div className="relative w-full sm:w-32 h-32 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0">
                              <Hotel className="w-12 h-12 text-slate-400" strokeWidth={1.5} />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Badge className={`${statusConfig.color} text-white px-3 py-1 text-xs font-semibold flex items-center gap-1.5`}>
                                      <StatusIcon className="w-3.5 h-3.5" />
                                      {statusConfig.text}
                                    </Badge>
                                    {labelConfig && (
                                      <Badge className={`${labelConfig.color} text-white px-3 py-1 text-xs font-semibold flex items-center gap-1.5`}>
                                        <labelConfig.icon className="w-3.5 h-3.5" />
                                        {labelConfig.text}
                                      </Badge>
                                    )}
                                  </div>
                                  <h3 className="text-lg sm:text-2xl font-bold text-slate-900 mb-2">
                                    {deal.property_name || 'Property'}
                                  </h3>
                                  <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                                    <div className="flex items-center gap-1.5">
                                      <MapPin className="w-4 h-4 text-blue-500" />
                                      <span className="font-medium">{deal.city}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <Calendar className="w-4 h-4 text-blue-500" />
                                      <span>{deal.check_in} → {deal.check_out}</span>
                                    </div>
                                  </div>
                                  {deal.amenities && (
                                    <div className="flex flex-wrap gap-2">
                                      {deal.amenities.slice(0, 4).map((amenity, idx) => (
                                        <span key={idx} className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full font-medium">
                                          {amenity}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right">
                                  <div className="text-4xl font-black text-blue-600 mb-1">€{deal.price_per_night}</div>
                                  <div className="text-sm text-slate-500 mb-2">
                                    <span className="line-through">€{deal.original_price}</span>
                                  </div>
                                  <div className="inline-flex items-center gap-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                                    <TrendingDown className="w-3.5 h-3.5" />
                                    {deal.discount_percentage}% off
                                  </div>
                                </div>
                              </div>
                              {!isConcluded && (
                                <Button 
                                  onClick={() => handleBookNow(deal)}
                                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 h-12 text-base font-semibold shadow-lg shadow-blue-500/30 mt-4"
                                >
                                  {isAuthenticated() && isTraveler() ? t('deals.bookNow') : 'Login per Prenotare'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Requests Section for Property Owners */}
            {userType === 'property' && (
              <div id="requests-section" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-emerald-600" />
                <h2 className="text-3xl font-bold text-slate-900">Richieste Viaggiatori</h2>
              </div>
              
              {loading ? (
                <div className="text-center py-20">
                  <div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-600 rounded-full animate-spin mx-auto"></div>
                </div>
              ) : getFilteredRequests().length === 0 ? (
                <Card className="p-16 text-center bg-white/50">
                  <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 text-lg">
                    {requests.length === 0 ? 'Nessuna richiesta disponibile al momento.' : 'Nessuna richiesta trovata con i filtri attuali. Prova a modificare la ricerca.'}
                  </p>
                  {hasActiveFilters() && (
                    <Button 
                      variant="outline" 
                      onClick={clearAllFilters}
                      className="mt-4"
                    >
                      Rimuovi tutti i filtri
                    </Button>
                  )}
                </Card>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                    {getFilteredRequests().map((request) => {
                      const statusConfig = getStatusConfig(request.status || 'APERTA');
                      const isConcluded = request.status === 'CONCLUSO';
                      const isInTreatment = request.status === 'IN_TRATTATIVA';
                      const StatusIcon = statusConfig.icon;

                      return (
                        <Card
                          key={request.id}
                          className={`group backdrop-blur-sm bg-white/80 border border-slate-200/50 p-3 sm:p-6 transition-all duration-300 ${
                            isConcluded 
                              ? 'opacity-50' 
                              : 'hover:shadow-2xl hover:scale-[1.01] hover:border-emerald-300/50'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row items-start justify-between mb-3 sm:mb-4 gap-2">
                            <div className="flex items-center gap-3">
                              <Badge className={`${statusConfig.color} text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5`}>
                                <StatusIcon className="w-3.5 h-3.5" />
                                {statusConfig.text}
                              </Badge>
                              <span className="text-sm text-slate-500">
                                {new Date(request.created_at).toLocaleDateString('it-IT', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <div className="text-left sm:text-right">
                              <div className="text-xl sm:text-2xl font-black text-emerald-600">
                                €{request.budget_min || 0} - €{request.budget_max || 0}
                              </div>
                              <div className="text-xs text-slate-500 font-medium">range budget/notte</div>
                            </div>
                          </div>
                          
                          <h3 className="text-lg sm:text-2xl font-bold text-slate-900 mb-3 sm:mb-4">{request.traveler_name || 'Viaggiatore'}</h3>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-emerald-500" />
                              <span className="font-medium">{request.city}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-emerald-500" />
                              <span>{request.check_in_date} → {request.check_out_date}</span>
                            </div>
                            {request.guests && (
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-emerald-500" />
                                <span>{request.guests} {request.guests === 1 ? 'persona' : 'persone'}</span>
                              </div>
                            )}
                          </div>
                          
                          {request.notes && (
                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                              <p className="text-sm text-slate-700 leading-relaxed">
                                <span className="font-semibold text-slate-900">Note:</span> {request.notes}
                              </p>
                            </div>
                          )}
                          
                          {!isConcluded && !isInTreatment && (
                            <Button
                              onClick={() => {
                                if (!isAuthenticated()) {
                                  setShowPropertyLoginModal(true);
                                } else {
                                  setSelectedRequest(request);
                                  setShowOfferModal(true);
                                }
                              }}
                              className="w-full h-12 font-semibold shadow-lg bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-emerald-500/30"
                            >
                              <Euro className="w-4 h-4 mr-1" /> Fai un Offerta
                            </Button>
                          )}
                          {isInTreatment && (
                            <Button variant="outline" className="w-full h-12 border-2">
                              Visualizza Chat
                            </Button>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Role Selection Modal */}
      <RoleSelectionModal
        open={showRoleSelectionModal}
        onClose={() => setShowRoleSelectionModal(false)}
        onRoleSelected={handleRoleSelection}
      />

      {/* Traveler Login Modal */}
      <TravelerLoginModal
        open={showTravelerLoginModal}
        onClose={() => setShowTravelerLoginModal(false)}
        onSuccess={handleTravelerLoginSuccess}
        onRegisterClick={() => {
          setShowTravelerLoginModal(false);
          navigate('/traveler/register');
        }}
      />

      {/* Property Login Modal */}
      <PropertyLoginModal
        open={showPropertyLoginModal}
        onClose={() => setShowPropertyLoginModal(false)}
        onSuccess={handlePropertyLoginSuccess}
        onRegisterClick={() => {
          setShowPropertyLoginModal(false);
          navigate('/property/register');
        }}
      />

      {/* Booking Modal */}
      <BookingModal
        deal={selectedDeal}
        open={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        onSuccess={handleBookingSuccess}
      />

      {/* Create Offer Modal (Property Owners) */}
      <CreateOfferModal
        open={showOfferModal}
        onClose={() => {
          setShowOfferModal(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onSuccess={() => {
          // Refresh requests to update UI
          fetchAllData();
        }}
      />

      {/* Create Request Modal (Travelers) */}
      <CreateRequestModal
        open={showCreateRequestModal}
        onClose={() => setShowCreateRequestModal(false)}
        onSuccess={() => {
          fetchAllData();
          toast.success('Richiesta creata! Riceverai offerte dai property owners.');
        }}
      />

      {/* Switch Role Button - Mobile Only */}
      {userType && (
        <button
          onClick={() => {
            setUserType(null);
            setSessionId(null);
            setActiveFilters({ city: null, budget: null, searchTerm: null });
            setChatMessages([{
              type: 'bot',
              text: t('chat.welcomeMessage')
            }]);
          }}
          className="sm:hidden fixed bottom-4 left-4 w-14 h-14 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-full shadow-2xl hover:shadow-slate-500/50 transition-all duration-300 flex items-center justify-center group z-40"
          title="Cambia Ruolo"
        >
          <svg 
            className="w-6 h-6 group-hover:rotate-180 transition-transform duration-300" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" 
            />
          </svg>
        </button>
      )}

      {/* Floating Action Button */}
      {userType && isAuthenticated() && (
        <button
          onClick={() => {
            if (userType === 'traveler') {
              setShowCreateRequestModal(true);
            } else {
              toast.info('Feature in arrivo: crea deal per property owners');
            }
          }}
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-full shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 flex items-center justify-center group z-40"
          title={userType === 'traveler' ? 'Crea Nuova Richiesta' : 'Crea Nuovo Deal'}
        >
          <Plus className="w-7 h-7 sm:w-8 sm:h-8 group-hover:rotate-90 transition-transform duration-300" />
        </button>
      )}
    </div>
  );
};

export default UnifiedMarketplace;